import { PureComponent } from 'react';
import cx from 'classnames';
import Button from 'core/components/Button';
import styles from './index.styl';
import PropTypes from 'prop-types';

/**
 * @author Aleksey Rezakov <a.rezakov@rambler-co.ru>
 */
export default function withTabs(props) {
  const {
    tabs,
    indent = 20,
    justifyContent = 'flex-start',
    after,
    before,
    beforeGap = 10,
    beforeFullwidth,
    wrap,
    activeButtonType = 'primary',
    inactiveButtonType = 'ghost',
    tabComponent: TabComponent = Button,
    useScrollerOnMobile,
  } = props;

  return function(Component) {
    class WithTabs extends PureComponent {
      static propTypes = {
        /**
         * Массив обьектов с табами. Объекты должны содержать свойство title, и свойства,
         * которые будут прокинуты в оборачиваемый компонент
         */
        tabs: PropTypes.arrayOf(PropTypes.object),

        /**
         * Расстояние от табов до контента в пикселях
         */
        indent: PropTypes.number,

        /**
         * Контент, который будет вставляться после табов
         */
        after: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.element,
        ]),

        /**
         * Контент, который будет вставлятсья до табов
         */
        before: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.element,
        ]),

        /**
         * Расстояние от табов до контента, который будет вставлятся до них. В пикселях
         */
        beforeGap: PropTypes.number,

        /**
         * Если true, то к табам будет применено css-правило flex-wrap: 'wrap', в противном случае - flex-wrap: 'nowrap'
         */
        wrap: PropTypes.bool,

        /**
         * Включение Scroller на мобилке
         */
        useScrollerOnMobile: PropTypes.bool,

        /**
         * Компонент, используемый для отрисовки заголовков табов
         */
        tabComponent: PropTypes.func,

        /**
         * Можно переопределить тип кнопки в активном состоянии.<br/>
         * По умолчанию `primary`
         */
        activeButtonType: PropTypes.string,

        /**
         * Можно переопределить тип кнопки в не активном состоянии.<br/>
         * По умолчанию `ghost`
         */
        inactiveButtonType: PropTypes.string,

        /**
         * Свойство flex-box.<br />
         * Позволяет позиционировать "табы" по горизонтали.
         */
        justifyContent: PropTypes.string,
      };

      state = {
        active: 0,
      };

      render() {
        const { active } = this.state;
        const {
          title: activeTabTitle, // eslint-disable-line
          ...tabsProps
        } = tabs[active];

        return (
          <div>
            <style jsx>{`
              .tabs-wrapper
                flex-wrap ${wrap ? 'wrap' : 'nowrap'}
                justify-content ${justifyContent}
            `}
            </style>
            {tabs.length > 0 && (
              <div style={{ ...indent && { marginBottom: `${indent}px` } }}>
                <div>
                  <div className={cx(styles.tabsWrapper, 'tabs-wrapper', useScrollerOnMobile && styles.scroller)}>
                    {before &&
                      <div style={{ marginRight: `${beforeGap}px`, ...beforeFullwidth && { width: '100%' } }}>{before}</div>
                    }
                    {tabs.map((tab, index) => {
                      const isActive = active === index;

                      const {
                        title,
                        ...tabProps
                      } = tab;
                      return (
                        <TabComponent
                          size='small'
                          {...tabProps}
                          key={index}
                          type={isActive ? activeButtonType : inactiveButtonType}
                          state={isActive ? 'idle' : 'auto'}
                          inactive={isActive}
                          onClick={() => {this.setState({ active: index });}}
                        >
                          {title}
                        </TabComponent>
                      );
                    })}
                    {after}
                  </div>
                </div>
              </div>
            )}
            <Component
              activeIndex={active}
              {...this.props}
              {...tabsProps}
            />
          </div>
        );
      }
    }

    WithTabs.WrappedComponent = Component;

    return WithTabs;
  };
}
